import React, { useState } from "react";
import * as API from "../api";
import {
  BG,
  BLUE,
  BLUE_LIGHT,
  BORDER,
  TEXT,
  TEXT2,
  TEXT3,
  CARD,
  WHITE,
  SUCCESS,
  DANGER,
  SHADOW,
  SHADOW_SM,
  SHADOW_LG,
  FONT,
  MONO,
  BG_LIQUID_RADIAL,
  LIQUID_GLOBAL_CSS,
  LIQUID_GLOBAL_CSS_PRO,
  liquidSurface,
  liquidSurfaceElevated,
  LivingVellaBackground,
  LIVING_BG_CSS,
  SilverSpatialBackground,
  SILVER_SPATIAL_BG_CSS,
} from "../shared";

// ===========================================
// PRICING SCREEN (shown after signup, before onboarding)
// ===========================================
function PricingScreen({ onComplete }) {
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState("");
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      id: "basic",
      name: "Basic",
      price: annual ? 119 : 149.95,
      period: annual ? "/mo, billed yearly" : "/mo",
      desc: "Perfect for solo business owners getting started with AI marketing.",
      color: "#6B7280",
      features: [
        "AI Social Media Posts (Instagram, Facebook, TikTok)",
        "AI Ad Campaign Builder",
        "Inbox & Auto-Replies",
        "Basic Analytics Dashboard",
        "500 AI Credits / month",
        "2-day free trial",
      ],
    },
    {
      id: "standard",
      name: "Standard",
      price: annual ? 279 : 349.95,
      period: annual ? "/mo, billed yearly" : "/mo",
      desc: "For growing businesses that want full automation and SEO.",
      color: BLUE,
      popular: true,
      features: [
        "Everything in Basic",
        "Website SEO + AI Search Optimization",
        "Google Ads Integration",
        "Post Scheduling + Queue",
        "Live Performance Analytics",
        "1,500 AI Credits / month",
        "Priority Support",
        "2-day free trial",
      ],
    },
    {
      id: "premium",
      name: "Premium",
      price: annual ? 639 : 799,
      period: annual ? "/mo, billed yearly" : "/mo",
      desc: "For agencies and power users who need everything at scale.",
      color: "#7C3AED",
      features: [
        "Everything in Standard",
        "Multi-business management",
        "Advanced ROAS optimization",
        "Custom brand voice training",
        "Dedicated account manager",
        "Unlimited AI Credits",
        "White-glove onboarding",
        "2-day free trial",
      ],
    },
  ];

  const handleSelect = async (planId) => {
    if (!disclaimerAgreed) { setDisclaimerError("You must agree to the AI disclaimer before subscribing."); return; }
    setDisclaimerError("");
    setLoading(planId);
    setError("");
    try {
      const data = await API.createCheckout(planId);
      if (data?.url) {
        window.location.href = data.url;
      } else if (!process.env.REACT_APP_API_URL) {
        // Local dev only — no real backend configured, skip straight to
        // onboarding so we can iterate on the wizard without a Stripe key.
        onComplete(planId);
      } else {
        // Production: a missing Stripe URL is a real failure. Don't grant
        // access; show the error so the user can retry / contact support.
        setError("Stripe checkout isn't available right now. Please try again, or contact support if it keeps happening.");
      }
    } catch (err) {
      setError(err?.message || "Payment setup failed. Please try again.");
      // Local dev only — let us proceed without Stripe.
      if (!process.env.REACT_APP_API_URL) onComplete(planId);
    }
    setLoading(null);
  };

  return (
    <div style={{ minHeight: "100vh", background: "transparent", padding: "40px 20px", fontFamily: FONT, position: "relative", isolation: "isolate", overflow: "hidden" }}>
      <style>{(LIQUID_GLOBAL_CSS_PRO || LIQUID_GLOBAL_CSS) + SILVER_SPATIAL_BG_CSS}</style>
      {/* iPhone 17 Pro Silver — semi-spatial liquid-chrome backdrop */}
      <SilverSpatialBackground />

      <div style={{ maxWidth: 1060, margin: "0 auto", position: "relative", zIndex: 1, animation: "slideUp 0.5s ease" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: TEXT, letterSpacing: 1 }}>Vella</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#0284C7", background: "rgba(255,255,255,0.65)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(56,189,248,0.45)", borderRadius: 999, padding: "2px 12px", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 6px 18px -6px rgba(14,165,233,0.4)" }}>2.0</span>
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: TEXT, marginBottom: 10, lineHeight: 1.2, letterSpacing: "-0.025em" }}>
            Choose Your Plan
          </h1>
          <p style={{ fontSize: 16, color: TEXT2, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px" }}>
            Start your 2-day free trial today. No credit card charged until your trial ends. Cancel anytime.
          </p>

          {/* Annual toggle — liquid glass */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, ...liquidSurface, borderRadius: 999, padding: "6px 16px" }}>
            <span style={{ fontSize: 13, fontWeight: annual ? 400 : 600, color: annual ? TEXT3 : TEXT }}>Monthly</span>
            <div onClick={() => setAnnual(p => !p)} style={{ width: 42, height: 24, borderRadius: 12, background: annual ? "linear-gradient(135deg, #38BDF8, #0EA5E9 60%, #0284C7)" : "rgba(180,205,235,0.55)", cursor: "pointer", position: "relative", transition: "background 0.25s", boxShadow: annual ? "0 4px 12px rgba(14,165,233,0.35), inset 0 1px 0 rgba(255,255,255,0.3)" : "inset 0 1px 2px rgba(11,107,211,0.15)" }}>
              <div style={{ position: "absolute", top: 3, left: annual ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: WHITE, boxShadow: "0 2px 6px rgba(11,107,211,0.25)", transition: "left 0.25s" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: annual ? 600 : 400, color: annual ? TEXT : TEXT3 }}>
              Annual <span style={{ fontSize: 11, fontWeight: 700, color: SUCCESS, background: `${SUCCESS}1F`, borderRadius: 999, padding: "1px 8px", marginLeft: 4 }}>Save 20%</span>
            </span>
          </div>
        </div>

        {/* AI Disclaimer */}
            <div style={{ background: "rgba(255,251,235,0.65)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", border: "1px solid rgba(245,158,11,0.45)", borderRadius: 16, padding: 16, marginBottom: 24, boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 8px 22px -8px rgba(245,158,11,0.18)" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={disclaimerAgreed} onChange={e => { setDisclaimerAgreed(e.target.checked); setDisclaimerError(""); }} style={{ marginTop: 3, width: 18, height: 18, accentColor: "#4ECDC4" }} />
                <span style={{ fontSize: 13, color: "#1A1A2E", lineHeight: 1.5 }}>
                  I understand that Vella uses AI-generated recommendations. Mancini Business Consulting LLC is not liable for outcomes resulting from AI-suggested actions. By subscribing, I agree to use AI features at my own discretion and accept full responsibility for business decisions made using this platform.
                </span>
              </label>
              {disclaimerError && <p style={{ color: "#FF6B6B", fontSize: 12, marginTop: 8, marginLeft: 28 }}>{disclaimerError}</p>}
            </div>
            {/* Plan Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 20, marginBottom: 40 }}>
          {plans.map((plan) => (
            <div key={plan.id} className="liquid-hover"
              style={{
                ...liquidSurfaceElevated,
                background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
                backdropFilter: "blur(34px) saturate(180%)",
                WebkitBackdropFilter: "blur(34px) saturate(180%)",
                borderRadius: 24, padding: 32,
                border: plan.popular ? "1px solid rgba(14,165,233,0.55)" : "1px solid rgba(255,255,255,0.40)",
                boxShadow: plan.popular ? `0 0 0 1px rgba(14,165,233,0.4), 0 22px 60px rgba(11,107,211,0.18), 0 1px 0 rgba(255,255,255,0.7) inset` : "0 1px 0 rgba(255,255,255,0.7) inset, 0 22px 60px -22px rgba(11,107,211,0.20), 0 8px 24px -8px rgba(14,165,233,0.12)",
                position: "relative",
              }}>

              {plan.popular && (
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg, #38BDF8, #0EA5E9 60%, #0284C7)", color: WHITE, fontSize: 11, fontWeight: 700, padding: "5px 18px", borderRadius: 999, letterSpacing: 1, whiteSpace: "nowrap", boxShadow: "0 6px 18px rgba(14,165,233,0.5), inset 0 1px 0 rgba(255,255,255,0.3)" }}>
                  MOST POPULAR
                </div>
              )}

              {/* Plan header */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: plan.color + "18", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: plan.color }} />
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>{plan.name}</h2>
                <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>{plan.desc}</p>
              </div>

              {/* Price */}
              <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontSize: 42, fontWeight: 800, color: TEXT, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.025em" }}>${plan.price}</span>
                  <span style={{ fontSize: 13, color: TEXT3 }}>{plan.period}</span>
                </div>
                <p style={{ fontSize: 11, color: SUCCESS, fontWeight: 600, marginTop: 4 }}>2-day free trial included</p>
              </div>

              {/* Features */}
              <div style={{ marginBottom: 28 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                      <circle cx="12" cy="12" r="12" fill={plan.color + "18"}/>
                      <path d="M7 12l3.5 3.5L17 9" stroke={plan.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => handleSelect(plan.id)}
                disabled={loading === plan.id || !disclaimerAgreed}
                style={{
                  width: "100%", padding: "14px", borderRadius: 999,
                  background: plan.popular
                    ? "linear-gradient(135deg, #38BDF8, #0EA5E9 60%, #0284C7)"
                    : plan.id === "premium"
                      ? "linear-gradient(135deg, #0284C7, #0B6BD3 60%, #0A4FA8)"
                      : "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
                  color: plan.popular || plan.id === "premium" ? WHITE : TEXT,
                  border: plan.popular || plan.id === "premium" ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(255,255,255,0.40)",
                  backdropFilter: plan.popular || plan.id === "premium" ? "none" : "blur(34px) saturate(180%)",
                  WebkitBackdropFilter: plan.popular || plan.id === "premium" ? "none" : "blur(34px) saturate(180%)",
                  opacity: !disclaimerAgreed ? 0.5 : loading === plan.id ? 0.6 : 1,
                  cursor: !disclaimerAgreed ? "not-allowed" : loading === plan.id ? "wait" : "pointer",
                  fontSize: 15, fontWeight: 700,
                  fontFamily: FONT,
                  transition: "all 0.25s", boxShadow: plan.popular || plan.id === "premium" ? "0 12px 28px rgba(14,165,233,0.40), inset 0 1px 0 rgba(255,255,255,0.30)" : "0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(11,107,211,0.18)"
                }}>
                {loading === plan.id ? "Redirecting to checkout..." : `Start Free Trial -- ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        {/* Subscription disclaimer (below plan cards) */}
        <p style={{ textAlign: "center", fontSize: 12, color: TEXT3, lineHeight: 1.6, maxWidth: 640, margin: "0 auto 24px" }}>
          Mancini Business Consulting LLC is not responsible for AI-generated content or decisions made by Vella. By subscribing, you acknowledge that AI outputs should be reviewed before use.
        </p>

        {error && <p style={{ textAlign: "center", color: DANGER, fontSize: 13, marginBottom: 20 }}>{error}</p>}

        {/* Trust bar */}
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28, flexWrap: "wrap", marginBottom: 16 }}>
            {["No credit card until trial ends", "Cancel anytime", "Secure checkout via Stripe"].map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: TEXT3 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4" stroke={SUCCESS} strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="10" stroke={SUCCESS} strokeWidth="1.5"/>
                </svg>
                {t}
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: TEXT3 }}>
            Already have an account?{" "}
            <span onClick={() => window.location.reload()} style={{ color: BLUE, cursor: "pointer", fontWeight: 600 }}>Sign in</span>
          </p>
        </div>
        {/* AI Disclaimer */}
        <div style={{ marginTop: 24, padding: "16px 20px", ...liquidSurface, borderRadius: 16, maxWidth: 700, margin: "24px auto 0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TEXT, marginBottom: 6 }}>AI Disclaimer & Limitation of Liability</div>
          <p style={{ fontSize: 11, color: TEXT3, lineHeight: 1.6, margin: 0 }}>
            Vella 2.0 is an AI-powered marketing assistant. AI-generated content, recommendations, analytics, and creative assets may contain errors, inaccuracies, or may not be suitable for all purposes. All AI outputs should be reviewed by the user before publishing, distribution, or reliance upon. By subscribing to and using Vella 2.0, you acknowledge and agree that: (1) AI-generated content can be inaccurate and should not be relied upon as the sole basis for business decisions; (2) Mancini Business Consulting LLC, its officers, directors, employees, and affiliates are not responsible for any errors, omissions, damages, losses, or wrongdoing arising from the use of AI-generated content, recommendations, or creative assets produced by Vella 2.0; (3) The user assumes full responsibility for reviewing, verifying, and approving all AI-generated outputs before use; (4) Mancini Business Consulting LLC makes no warranties, express or implied, regarding the accuracy, completeness, or fitness for a particular purpose of any AI-generated content. Use of this service constitutes acceptance of these terms.
          </p>
          <p style={{ fontSize: 10, color: TEXT3, marginTop: 8, margin: 0, marginTop: 8 }}>&copy; {new Date().getFullYear()} Mancini Business Consulting LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default PricingScreen;
